import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Loader2, Eye, EyeOff, ArrowRight, Clock, Users, BarChart3, UserPlus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { RotalieLogo } from "../components/RotalieLogo";
import api from "../lib/api";

const STAFF_FLOW_STORAGE_KEY = 'rotalie_staff_login_preference';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, staffLogin, staffEmailLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState("admin");
  
  // Check for invite params
  const isInvite = searchParams.get('invite') === 'true';
  const inviteCode = searchParams.get('code') || "";
  const inviteEmpId = searchParams.get('empId') || "";
  const inviteEmail = searchParams.get('email') || "";
  const typeParam = searchParams.get('type');
  
  // Staff sub-flow: "choose" | "new" | "create" | "returning"
  // Load preference from localStorage, but override for invites
  const [staffFlow, setStaffFlow] = useState(() => {
    if (isInvite) return 'new'; // Invites go to new staff flow
    const saved = localStorage.getItem(STAFF_FLOW_STORAGE_KEY);
    return saved === 'returning' ? 'returning' : 'choose';
  });
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [staffPassword, setStaffPassword] = useState("");
  
  // Staff account creation fields
  const [staffEmail, setStaffEmail] = useState("");
  const [staffNewPassword, setStaffNewPassword] = useState("");
  const [staffConfirmPassword, setStaffConfirmPassword] = useState("");
  const [verifiedEmployee, setVerifiedEmployee] = useState(null);
  
  // Staff returning user fields
  const [returningEmail, setReturningEmail] = useState("");
  const [returningPassword, setReturningPassword] = useState("");

  // Handle invite link - pre-fill fields and auto-switch to staff tab
  useEffect(() => {
    if (typeParam === 'staff' || isInvite) {
      setLoginType('staff');
    }
    
    if (isInvite && inviteCode && inviteEmpId) {
      // Pre-fill the fields from invite
      setCompanyCode(inviteCode);
      setEmployeeId(inviteEmpId);
      if (inviteEmail) {
        setStaffEmail(decodeURIComponent(inviteEmail));
      }
      setStaffFlow('new');
      
      // Auto-verify the employee
      const autoVerify = async () => {
        try {
          const response = await api.post('/auth/staff-verify', {
            company_code: inviteCode,
            employee_id: inviteEmpId
          });
          
          if (response.data.has_login_method) {
            toast.info("You already have an account. Please sign in.");
            setStaffFlow("returning");
            setReturningEmail(decodeURIComponent(inviteEmail));
          } else {
            setVerifiedEmployee(response.data);
            toast.success(`Welcome, ${response.data.employee_name}! Just set your password to get started.`);
            setStaffFlow("create");
          }
        } catch (error) {
          toast.error("Could not verify invite. Please check your details.");
        }
      };
      
      autoVerify();
    }
  }, [typeParam, isInvite, inviteCode, inviteEmpId, inviteEmail]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      toast.success(`Welcome back${result.user.name ? ', ' + result.user.name : ''}!`);
      if (result.user.is_platform_admin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Staff login with company code + employee ID (for new accounts that still need old method)
  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await staffLogin(companyCode, employeeId, staffPassword);
      toast.success(`Welcome back, ${result.user.name}!`);
      // Save preference for next time
      localStorage.setItem(STAFF_FLOW_STORAGE_KEY, 'returning');
      
      // Check if they have admin dashboard access
      if (result.permissions?.can_access_admin_dashboard) {
        navigate("/dashboard");
      } else {
        navigate("/staff");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Staff login with email (for returning users)
  const handleReturningStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await staffEmailLogin(returningEmail, returningPassword);
      toast.success(`Welcome back, ${result.user.name}!`);
      // Save preference for next time
      localStorage.setItem(STAFF_FLOW_STORAGE_KEY, 'returning');
      
      // Check if they have admin dashboard access
      if (result.permissions?.can_access_admin_dashboard) {
        navigate("/dashboard");
      } else {
        navigate("/staff");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  // Verify staff credentials (company code + employee ID)
  const handleVerifyStaff = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/staff-verify', {
        company_code: companyCode,
        employee_id: employeeId
      });
      
      if (response.data.has_login_method) {
        // Already has account - redirect to returning user login
        toast.warning("You already have an account. Please use 'I already have an account' to log in.");
        localStorage.setItem(STAFF_FLOW_STORAGE_KEY, 'returning');
        setStaffFlow("returning");
      } else {
        // No account yet - allow them to create one
        setVerifiedEmployee(response.data);
        toast.success(`Found your profile, ${response.data.employee_name}! Create your login credentials.`);
        setStaffFlow("create");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not verify your details. Please check your Company Code and Employee ID.");
    } finally {
      setLoading(false);
    }
  };

  // Create staff account
  const handleCreateStaffAccount = async (e) => {
    e.preventDefault();
    
    if (!staffEmail || !staffEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (staffNewPassword !== staffConfirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (staffNewPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      // Create the account
      const createResponse = await api.post('/auth/staff-create-account', {
        company_code: companyCode,
        employee_id: employeeId,
        email: staffEmail,
        password: staffNewPassword
      });
      
      toast.success(`Account created! Welcome, ${createResponse.data.employee_name}!`);
      
      // Set preference for returning users
      localStorage.setItem(STAFF_FLOW_STORAGE_KEY, 'returning');
      
      // Auto-login with the credentials they just set
      try {
        await staffEmailLogin(staffEmail, staffNewPassword);
        navigate("/staff");
      } catch (loginError) {
        // If auto-login fails, redirect to login page
        toast.info("Please sign in with your new credentials.");
        setReturningEmail(staffEmail);
        setStaffFlow("returning");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  // Reset staff flow when switching tabs
  const handleTabChange = (value) => {
    setLoginType(value);
    if (value === "staff") {
      // Load saved preference from localStorage
      const saved = localStorage.getItem(STAFF_FLOW_STORAGE_KEY);
      setStaffFlow(saved === 'returning' ? 'returning' : 'choose');
      setVerifiedEmployee(null);
      setCompanyCode("");
      setEmployeeId("");
      setStaffPassword("");
      setStaffEmail("");
      setStaffNewPassword("");
      setStaffConfirmPassword("");
      setReturningEmail("");
      setReturningPassword("");
    }
  };

  // Helper to switch between new/returning staff
  const switchStaffMode = (mode) => {
    if (mode === 'returning') {
      localStorage.setItem(STAFF_FLOW_STORAGE_KEY, 'returning');
      setStaffFlow('returning');
    } else {
      localStorage.removeItem(STAFF_FLOW_STORAGE_KEY);
      setStaffFlow('new');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row">
      {/* Left Side - Value Proposition */}
      <div className="hidden lg:flex lg:w-1/2 p-8 xl:p-16 items-center justify-center">
        <div className="w-full max-w-xl">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center gap-3 mb-16">
            <RotalieLogo size="large" />
          </Link>

          {/* Main Value Card */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-lg p-10 xl:p-12">
            <h2 className="text-4xl xl:text-5xl font-bold text-slate-900 font-['Inter'] leading-tight mb-6">
              Your team is waiting.
              <br />
              <span className="bg-gradient-to-r from-violet-600 to-teal-500 bg-clip-text text-transparent">Let&apos;s get organized.</span>
            </h2>
            
            <p className="text-xl text-slate-500 leading-relaxed mb-10">
              Stop wasting hours on spreadsheets. Start building smarter schedules in minutes.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-violet-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">5hrs</p>
                <p className="text-sm text-slate-500">Saved weekly</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">500+</p>
                <p className="text-sm text-slate-500">Teams trust us</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-6 h-6 text-violet-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900">90%</p>
                <p className="text-sm text-slate-500">Less errors</p>
              </div>
            </div>
          </div>

          <p className="text-slate-400 text-sm mt-10">
            © {new Date().getFullYear()} Rotalie. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col lg:bg-white lg:border-l lg:border-slate-200">
        {/* Mobile Header */}
        <header className="lg:hidden p-6">
          <Link to="/">
            <RotalieLogo />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 font-['Inter'] mb-2">Welcome back</h1>
              <p className="text-slate-500">Sign in to continue to your dashboard</p>
            </div>

            <Tabs value={loginType} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100 p-1 rounded-xl h-12">
                <TabsTrigger 
                  value="admin" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium text-base"
                  data-testid="admin-login-tab"
                >
                  Admin
                </TabsTrigger>
                <TabsTrigger 
                  value="staff" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium text-base"
                  data-testid="staff-login-tab"
                >
                  Staff
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="admin">
                <form onSubmit={handleAdminLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                      data-testid="email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 pr-12 rounded-xl transition-colors"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <div className="flex justify-end">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-violet-600 hover:text-violet-700 font-medium transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-lg shadow-violet-500/25"
                    disabled={loading}
                    data-testid="login-button"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Signing in...</>
                    ) : (
                      <>Sign in<ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="staff">
                {/* Staff flow: choose -> new/returning -> create/login */}
                
                {/* Choose mode (if no localStorage preference) */}
                {staffFlow === "choose" && (
                  <div className="space-y-4">
                    <p className="text-center text-slate-600 mb-6">
                      How would you like to sign in?
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-14 border-2 border-slate-200 hover:border-violet-300 hover:bg-violet-50 rounded-xl transition-all duration-200"
                      onClick={() => switchStaffMode('new')}
                      data-testid="staff-new-btn"
                    >
                      <UserPlus className="w-5 h-5 mr-3 text-violet-600" />
                      <div className="text-left">
                        <span className="font-semibold text-slate-900">I&apos;m a new staff member</span>
                        <p className="text-xs text-slate-500 font-normal">Set up your account for the first time</p>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-14 border-2 border-slate-200 hover:border-teal-300 hover:bg-teal-50 rounded-xl transition-all duration-200"
                      onClick={() => switchStaffMode('returning')}
                      data-testid="staff-returning-btn"
                    >
                      <KeyRound className="w-5 h-5 mr-3 text-teal-600" />
                      <div className="text-left">
                        <span className="font-semibold text-slate-900">I already have an account</span>
                        <p className="text-xs text-slate-500 font-normal">Sign in with your email</p>
                      </div>
                    </Button>
                  </div>
                )}

                {/* Returning Staff - Email + Password login */}
                {staffFlow === "returning" && (
                  <form onSubmit={handleReturningStaffLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="returningEmail" className="text-slate-700 font-medium">Email Address</Label>
                      <Input
                        id="returningEmail"
                        type="email"
                        placeholder="Enter your email"
                        value={returningEmail}
                        onChange={(e) => setReturningEmail(e.target.value)}
                        required
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                        data-testid="returning-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="returningPassword" className="text-slate-700 font-medium">Password</Label>
                      <div className="relative">
                        <Input
                          id="returningPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={returningPassword}
                          onChange={(e) => setReturningPassword(e.target.value)}
                          required
                          className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 pr-12 rounded-xl transition-colors"
                          data-testid="returning-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <div className="text-right">
                        <Link 
                          to="/forgot-password?type=staff" 
                          className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                          data-testid="staff-forgot-password-link"
                        >
                          Forgot password?
                        </Link>
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full h-12 bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-xl font-semibold text-base transition-all duration-200"
                      disabled={loading}
                      data-testid="staff-email-login-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Signing in...</>
                      ) : (
                        <>Sign in<ArrowRight className="w-5 h-5 ml-2" /></>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-sm"
                      onClick={() => switchStaffMode('new')}
                    >
                      New staff member? Create account
                    </Button>
                  </form>
                )}

                {/* New Staff - Step 1: Verify with Company Code + Employee ID */}
                {staffFlow === "new" && !verifiedEmployee && (
                  <form onSubmit={handleVerifyStaff} className="space-y-5">
                    <div className="bg-violet-50 rounded-xl p-4 mb-4">
                      <p className="text-sm text-violet-700">
                        <strong>Step 1:</strong> Enter the Company Code and Employee ID provided by your manager to verify your identity.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="verifyCompanyCode" className="text-slate-700 font-medium">Company Code</Label>
                      <Input
                        id="verifyCompanyCode"
                        type="text"
                        placeholder="e.g., ABC12345"
                        value={companyCode}
                        onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                        required
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 uppercase rounded-xl transition-colors"
                        data-testid="verify-company-code-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="verifyEmployeeId" className="text-slate-700 font-medium">Employee ID</Label>
                      <Input
                        id="verifyEmployeeId"
                        type="text"
                        placeholder="e.g., EMP001"
                        value={employeeId}
                        onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                        required
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 uppercase font-mono rounded-xl transition-colors"
                        data-testid="verify-employee-id-input"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full h-12 bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-xl font-semibold text-base transition-all duration-200"
                      disabled={loading}
                      data-testid="verify-staff-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Verifying...</>
                      ) : (
                        <>Verify My Details<ArrowRight className="w-5 h-5 ml-2" /></>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-sm"
                      onClick={() => switchStaffMode('returning')}
                    >
                      Already have an account? Sign in
                    </Button>
                  </form>
                )}

                {/* New Staff - Step 2: Set Email and Password */}
                {staffFlow === "create" && verifiedEmployee && (
                  <form onSubmit={handleCreateStaffAccount} className="space-y-5">
                    <div className="bg-teal-50 rounded-xl p-4 mb-4">
                      <p className="text-sm text-teal-700">
                        <strong>Step 2:</strong> Hi <span className="font-semibold">{verifiedEmployee.employee_name}</span>! Set up your login credentials below.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="staffEmail" className="text-slate-700 font-medium">Email Address</Label>
                      <Input
                        id="staffEmail"
                        type="email"
                        placeholder="Enter your email address"
                        value={staffEmail}
                        onChange={(e) => setStaffEmail(e.target.value)}
                        required
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                        data-testid="staff-email-input"
                      />
                      <p className="text-xs text-slate-500">We&apos;ll use this for login and important notifications</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="staffNewPassword" className="text-slate-700 font-medium">Password</Label>
                      <div className="relative">
                        <Input
                          id="staffNewPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="Create a password (min. 6 characters)"
                          value={staffNewPassword}
                          onChange={(e) => setStaffNewPassword(e.target.value)}
                          required
                          minLength={6}
                          className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 pr-12 rounded-xl transition-colors"
                          data-testid="staff-new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="staffConfirmPassword" className="text-slate-700 font-medium">Confirm Password</Label>
                      <Input
                        id="staffConfirmPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={staffConfirmPassword}
                        onChange={(e) => setStaffConfirmPassword(e.target.value)}
                        required
                        className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                        data-testid="staff-confirm-password-input"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full h-12 bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-xl font-semibold text-base transition-all duration-200"
                      disabled={loading}
                      data-testid="create-staff-account-btn"
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Creating Account...</>
                      ) : (
                        <>Create Account<ArrowRight className="w-5 h-5 ml-2" /></>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => { setVerifiedEmployee(null); setStaffFlow("new"); }}
                    >
                      ← Back
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            <p className="text-center text-slate-500 mt-8">
              Do not have an account?{" "}
              <Link to="/signup" className="text-violet-600 hover:text-violet-700 font-semibold transition-colors" data-testid="signup-link">
                Start free trial
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
